import React, { useEffect, useState } from 'react';
import classes from './MatchDetails2.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { FaArrowLeft, FaCircle } from 'react-icons/fa6';
import SmallLoader from '../../component/SmallLoader';
import { removeUserDetails } from '../../store/slices/userSlice';
import { matchDetailsApi } from '../../http/http';
import Lineups2 from '../../component/NewComponents/Lineups2';
import MatchStats2 from '../../component/NewComponents/MatchStats2';
import MatchEvents2 from '../../component/NewComponents/MatchEvents2';
import { useTranslation } from 'react-i18next';
import i18n from '../../locales/i18n';

const FT_STATUSES = ["FT", "AET", "PEN"];
const LIVE_STATUSES = ["1H", "2H", "ET", "P", "HT", "LIVE"];

const TABS = [
    { key: "events", label: "Events" },
    { key: "stats", label: "Stats" },
    { key: "lineups", label: "Lineups" },
];

const TABS_SW = [
    { key: "events", label: "Matukio" },
    { key: "stats", label: "Takwimu" },
    { key: "lineups", label: "Vikosi" },
];

const MatchDetails2 = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userDetails } = useSelector(s => s.persisted?.userSlice);

    const [matchDetails, setMatchDetails] = useState(null);
    const [matchDetails2, setMatchDetails2] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loader, setLoader] = useState(true);
    const [tab, setTab] = useState("events");

    const { t } = useTranslation();

    const { selectedLanguage } = useSelector((state) => state.languageSlice);

    useEffect(() => {
        if (selectedLanguage && i18n.language !== selectedLanguage) {
            i18n.changeLanguage(String(selectedLanguage));
        }
    }, [selectedLanguage, i18n]);

    /* ── fetch ── */
    const fetchMatch = async (showLoader) => {
        try {
            if (showLoader) setLoading(true);
            setLoader(true);
            const r = await matchDetailsApi(location?.state?.match_fixture_id, userDetails?.token);
            setMatchDetails(r?.data);
            if (r?.data?.match_details) setMatchDetails2(JSON.parse(r.data.match_details));
        } catch (e) {
            if (e?.response?.status === 403) {
                dispatch(removeUserDetails());
                toast.error(t("71"));
                navigate('/');
            } else {
                toast.error(e?.response?.data?.message || t("2"));
            }
        } finally {
            setLoading(false);
            setLoader(false);
        }
    };

    useEffect(() => {
        if (location?.state?.match_fixture_id) fetchMatch(true);
        else navigate("/home");
    }, [location?.state?.match_fixture_id]);

    useEffect(() => {
        if (!loader) {
            const id = setInterval(() => { if (!document.hidden) fetchMatch(false); }, 10000);
            return () => clearInterval(id);
        }
    }, [loader]);

    /* ── status ── */
    const getStatus = () => {
        const s = matchDetails?.match_status;
        const elapsed = matchDetails2?.fixture?.status?.elapsed;
        const extra = matchDetails2?.fixture?.status?.extra;
        if (FT_STATUSES.includes(s)) return { label: t("100"), live: false };
        if (s === "SUSP") return { label: t("101"), live: false };
        if (s === "PST") return { label: t("102"), live: false };
        if (s === "CANC") return { label: t("103"), live: false };
        if (["ABD", "AWD", "WO"].includes(s)) return { label: t("104"), live: false };
        if (s === "HT") return { label: `${t("105")} ${elapsed}'`, live: true };
        if (s === "ET") return { label: `${elapsed}' ${t("106")} +${extra}'`, live: true };
        if (s === "P") return { label: `${t("107")} ${elapsed}'`, live: true };
        if (LIVE_STATUSES.includes(s)) return { label: `${elapsed}'`, live: true };
        return null;
    };

    /* ── derived ── */
    const status = getStatus();
    const showScore = [...FT_STATUSES, ...LIVE_STATUSES].includes(matchDetails?.match_status);
    const homeGoals = matchDetails2?.goals?.home;
    const awayGoals = matchDetails2?.goals?.away;
    const homeWin = showScore && homeGoals > awayGoals;
    const awayWin = showScore && awayGoals > homeGoals;
    const isFT = FT_STATUSES.includes(matchDetails?.match_status);

    /* ── lineups ── */
    const lineups = matchDetails2?.lineups ?? [];
    const homeLineup = lineups[0] ?? null;
    const awayLineup = lineups[1] ?? null;

    /* ── stats ── */
    const stats = matchDetails2?.statistics ?? [];
    const homeStat = stats[0]?.statistics ?? [];
    const awayStat = stats[1]?.statistics ?? [];

    const getStat = (arr, type) => arr.find(s => s.type === type)?.value ?? 0;

    const teamStatistics = stats.length > 0 ? {
        homeTeamLogo: matchDetails?.home_team_image,
        homeTeamName: matchDetails?.home_team_name,
        awayTeamLogo: matchDetails?.away_team_image,
        awayTeamName: matchDetails?.away_team_name,

        homeTeamTotalShots: getStat(homeStat, "Total Shots"),
        awayTeamTotalShots: getStat(awayStat, "Total Shots"),
        homeTeamTotalShotsPer: getStat(homeStat, "Total Shots") / Math.max(getStat(homeStat, "Total Shots") + getStat(awayStat, "Total Shots"), 1) * 100,
        awayTeamTotalShotsPer: getStat(awayStat, "Total Shots") / Math.max(getStat(homeStat, "Total Shots") + getStat(awayStat, "Total Shots"), 1) * 100,

        homeTeamShotsOnGoal: getStat(homeStat, "Shots on Goal"),
        awayTeamShotsOnGoal: getStat(awayStat, "Shots on Goal"),
        homeTeamShotsOnGoalPer: getStat(homeStat, "Shots on Goal") / Math.max(getStat(homeStat, "Shots on Goal") + getStat(awayStat, "Shots on Goal"), 1) * 100,
        awayTeamShotsOnGoalPer: getStat(awayStat, "Shots on Goal") / Math.max(getStat(homeStat, "Shots on Goal") + getStat(awayStat, "Shots on Goal"), 1) * 100,

        homeTeamShotsOffGoal: getStat(homeStat, "Shots off Goal"),
        awayTeamShotsOffGoal: getStat(awayStat, "Shots off Goal"),
        homeTeamShotsOffGoalPer: getStat(homeStat, "Shots off Goal") / Math.max(getStat(homeStat, "Shots off Goal") + getStat(awayStat, "Shots off Goal"), 1) * 100,
        awayTeamShotsOffGoalPer: getStat(awayStat, "Shots off Goal") / Math.max(getStat(homeStat, "Shots off Goal") + getStat(awayStat, "Shots off Goal"), 1) * 100,

        homeTeamFouls: getStat(homeStat, "Fouls"),
        awayTeamFouls: getStat(awayStat, "Fouls"),
        homeTeamFoulsPer: getStat(homeStat, "Fouls") / Math.max(getStat(homeStat, "Fouls") + getStat(awayStat, "Fouls"), 1) * 100,
        awayTeamFoulsPer: getStat(awayStat, "Fouls") / Math.max(getStat(homeStat, "Fouls") + getStat(awayStat, "Fouls"), 1) * 100,

        homeTeamCorner: getStat(homeStat, "Corner Kicks"),
        awayTeamCorner: getStat(awayStat, "Corner Kicks"),
        homeTeamCornerPer: getStat(homeStat, "Corner Kicks") / Math.max(getStat(homeStat, "Corner Kicks") + getStat(awayStat, "Corner Kicks"), 1) * 100,
        awayTeamCornerPer: getStat(awayStat, "Corner Kicks") / Math.max(getStat(homeStat, "Corner Kicks") + getStat(awayStat, "Corner Kicks"), 1) * 100,

        homeTeamOffsides: getStat(homeStat, "Offsides"),
        awayTeamOffsides: getStat(awayStat, "Offsides"),
        homeTeamOffsidesPer: getStat(homeStat, "Offsides") / Math.max(getStat(homeStat, "Offsides") + getStat(awayStat, "Offsides"), 1) * 100,
        awayTeamOffsidesPer: getStat(awayStat, "Offsides") / Math.max(getStat(homeStat, "Offsides") + getStat(awayStat, "Offsides"), 1) * 100,

        homeTeamYellowCard: getStat(homeStat, "Yellow Cards"),
        awayTeamYellowCard: getStat(awayStat, "Yellow Cards"),
        homeTeamYellowCardPer: getStat(homeStat, "Yellow Cards") / Math.max(getStat(homeStat, "Yellow Cards") + getStat(awayStat, "Yellow Cards"), 1) * 100,
        awayTeamYellowCardPer: getStat(awayStat, "Yellow Cards") / Math.max(getStat(homeStat, "Yellow Cards") + getStat(awayStat, "Yellow Cards"), 1) * 100,

        homeTeamRedCard: getStat(homeStat, "Red Cards"),
        awayTeamRedCard: getStat(awayStat, "Red Cards"),
        homeTeamRedCardPer: getStat(homeStat, "Red Cards") / Math.max(getStat(homeStat, "Red Cards") + getStat(awayStat, "Red Cards"), 1) * 100,
        awayTeamRedCardPer: getStat(awayStat, "Red Cards") / Math.max(getStat(homeStat, "Red Cards") + getStat(awayStat, "Red Cards"), 1) * 100,

        homeTeamPossession: getStat(homeStat, "Ball Possession"),
        awayTeamPossession: getStat(awayStat, "Ball Possession"),

        homeTeamPasses: getStat(homeStat, "Total passes"),
        awayTeamPasses: getStat(awayStat, "Total passes"),
        homeTeamPassesPer: getStat(homeStat, "Passes %") || (getStat(homeStat, "Total passes") / Math.max(getStat(homeStat, "Total passes") + getStat(awayStat, "Total passes"), 1) * 100),
        awayTeamPassesPer: getStat(awayStat, "Passes %") || (getStat(awayStat, "Total passes") / Math.max(getStat(homeStat, "Total passes") + getStat(awayStat, "Total passes"), 1) * 100),
    } : null;

    /* ── goal events ── */
    const homeGoalEvents = matchDetails2?.events?.filter(
        d => d?.team?.name === matchDetails?.home_team_name && d?.type === "Goal" && d?.detail !== "Missed Penalty"
    ) ?? [];
    const awayGoalEvents = matchDetails2?.events?.filter(
        d => d?.team?.name === matchDetails?.away_team_name && d?.type === "Goal" && d?.detail !== "Missed Penalty"
    ) ?? [];

    if (loading) return (
        <div className={classes.page}>
            <div className={classes.loaderScreen}><SmallLoader /></div>
        </div>
    );

    return (
        <div className={classes.page}>
            <div className={classes.bgGlow} />
            <div className={classes.bgGrid} />

            {/* ══ STICKY HEADER ══ */}
            <div className={classes.stickyHeader}>

                {/* top nav */}
                <div className={classes.topNav}>
                    <button className={classes.backBtn} onClick={() => navigate(-1)} aria-label="Back">
                        <FaArrowLeft />
                    </button>
                    <div className={classes.topNavMid}>
                        <span className={classes.topNavTitle}>{t("116")}</span>
                        <span className={classes.topNavLeague}>{matchDetails?.league_name}</span>
                    </div>
                    <div style={{ width: 40 }} />
                </div>

                {/* match banner */}
                <div className={classes.matchBanner}>
                    <div className={classes.bannerGlow} />

                    {/* status chip */}
                    {status && (
                        <div className={`${classes.statusChip} ${status.live ? classes.statusChipLive : classes.statusChipFt}`}>
                            {status.live && <span className={classes.liveDot} />}
                            {status.label}
                        </div>
                    )}

                    {/* teams + score */}
                    <div className={classes.teamsRow}>
                        {/* home */}
                        <div className={classes.teamCol}>
                            <div className={`${classes.logoRing} ${homeWin ? classes.logoRingWinner : ""}`}>
                                <img src={matchDetails?.home_team_image} alt="" className={classes.teamLogo} />
                            </div>
                            <span className={classes.teamName}>{matchDetails?.home_team_name}</span>
                            {homeWin && <span className={classes.winnerChip}>{t("117")}</span>}
                        </div>

                        {/* centre */}
                        <div className={classes.scoreCentre}>
                            {showScore ? (
                                <>
                                    <div className={classes.scoreRow}>
                                        <span className={`${classes.scoreNum} ${awayWin ? classes.scoreNumDim : ""}`}>{homeGoals}</span>
                                        <span className={classes.scoreDash}>—</span>
                                        <span className={`${classes.scoreNum} ${homeWin ? classes.scoreNumDim : ""}`}>{awayGoals}</span>
                                    </div>
                                    {isFT && <span className={classes.ftLabel}>{t("118")}</span>}
                                </>
                            ) : (
                                <>
                                    <span className={classes.vsText}>{t("119")}</span>
                                    <span className={classes.kickoffDate}>
                                        {moment.utc(matchDetails?.match_date_time).local().format("ddd DD MMM")}
                                    </span>
                                    <span className={classes.kickoffTime}>
                                        {moment.utc(matchDetails?.match_date_time).local().format("HH:mm")}
                                    </span>
                                </>
                            )}
                        </div>

                        {/* away */}
                        <div className={classes.teamCol}>
                            <div className={`${classes.logoRing} ${awayWin ? classes.logoRingWinner : ""}`}>
                                <img src={matchDetails?.away_team_image} alt="" className={classes.teamLogo} />
                            </div>
                            <span className={classes.teamName}>{matchDetails?.away_team_name}</span>
                            {awayWin && <span className={classes.winnerChip}>{t("120")}</span>}
                        </div>
                    </div>

                    {/* venue */}
                    {matchDetails?.match_venue_name && (
                        <span className={classes.venueRow}>
                            📍 {matchDetails.match_venue_name}{matchDetails.match_venue_city ? `, ${matchDetails.match_venue_city}` : ""}
                        </span>
                    )}
                </div>

                {/* tabs */}
                <div className={classes.tabsRow}>
                    {(selectedLanguage==0 ? TABS : TABS_SW).map(t => (
                        <button
                            key={t.key}
                            type="button"
                            className={`${classes.tabBtn} ${tab === t.key ? classes.tabBtnActive : ""}`}
                            onClick={() => setTab(t.key)}
                        >
                            {t.label}
                            {tab === t.key && <span className={classes.tabUnderline} />}
                        </button>
                    ))}
                </div>
            </div>

            {/* ══ CONTENT ══ */}
            <main className={classes.content}>

                {/* Events tab */}
                {tab === "events" && (
                    <div className={classes.eventsPanel}>
                        {homeGoalEvents.length === 0 && awayGoalEvents.length === 0 ? (
                            <div className={classes.emptyState}>
                                <span>⚽</span>
                                <p>{t("121")}</p>
                            </div>
                        ) : (
                            <div className={classes.eventsGrid}>
                                <MatchEvents2
                                    team_name={matchDetails?.home_team_name}
                                    data={homeGoalEvents}
                                />
                                <MatchEvents2
                                    team_name={matchDetails?.away_team_name}
                                    data={awayGoalEvents}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Stats tab */}
                {tab === "stats" && (
                    <div className={classes.statsPanel}>
                        {teamStatistics ? (
                            <MatchStats2 teamStatistics={teamStatistics} />
                        ) : (
                            <div className={classes.emptyState}>
                                <span>📊</span>
                                <p>{t("122")}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Lineups tab */}
                {tab === "lineups" && (
                    <div className={classes.lineupsPanel}>
                        {homeLineup && awayLineup ? (
                            <Lineups2
                                homeTeam={homeLineup}
                                awayTeam={awayLineup}
                            />
                        ) : (
                            <div className={classes.emptyState}>
                                <span>👥</span>
                                <p>{t("123")}</p>
                            </div>
                        )}
                    </div>
                )}

            </main>
        </div>
    );
};

export default MatchDetails2;